"use client";

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Leaf, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useState } from 'react';

const navLinks = [
  { href: '/trees', label: 'Tree List' },
  { href: '/about', label: 'About' },
  { href: '/add-tree', label: 'Add a Tree' },
];

function NavLink({ href, label, className }: { href: string; label: string, className?: string }) {
  const pathname = usePathname();
  return (
    <Link
      href={href}
      className={cn(
        'transition-colors hover:text-primary',
        pathname === href ? 'text-primary font-semibold' : 'text-muted-foreground',
        className
      )}
    >
      {label}
    </Link>
  );
}


function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);

  const MobileNavLink = ({ href, label, className }: { href: string; label: string, className?: string }) => {
    const pathname = usePathname();
    return (
      <Link
        href={href}
        className={cn(
          'transition-colors hover:text-primary',
          pathname === href ? 'text-primary font-semibold' : 'text-muted-foreground',
          className
        )}
        onClick={() => setIsOpen(false)}
      >
        {label}
      </Link>
    );
  };

  return (
    <div className="flex-1 md:hidden">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" className="px-0">
            <Menu className="h-6 w-6" />
            <span className="sr-only">Toggle Menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left">
          <SheetHeader>
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          </SheetHeader>
          <Link href="/" className="mb-8 flex items-center" onClick={() => setIsOpen(false)}>
            <Leaf className="mr-2 h-6 w-6 text-primary" />
            <span className="font-bold font-headline">Local Canopy</span>
          </Link>
          <div className="flex flex-col space-y-4">
            {navLinks.map((link) => (
              <MobileNavLink key={link.href} {...link} className="text-lg" />
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default function Navbar() {
  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center">
        <div className="mr-4 hidden md:flex">
          <Link href="/" className="mr-6 flex items-center space-x-2">
            <Leaf className="h-6 w-6 text-primary" />
            <span className="hidden font-bold font-headline sm:inline-block">
              Local Canopy
            </span>
          </Link>
          <nav className="flex items-center space-x-6 text-sm font-medium">
            {navLinks.map((link) => (
              <NavLink key={link.href} {...link} />
            ))}
          </nav>
        </div>
        
        <MobileNav />

        <div className="flex flex-1 items-center justify-end">
            <Link href="/" className="md:hidden flex items-center space-x-2">
                <Leaf className="h-6 w-6 text-primary" />
                <span className="font-bold font-headline">Local Canopy</span>
            </Link>
        </div>
      </div>
    </header>
  );
}
